import { FC, MouseEvent as ReactMouseEvent, useCallback, useMemo, useRef } from "react";
import {
  DataGridPremium,
  DataGridPremiumProps,
  GridRowId
} from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router-dom";
import { makeQuickFilterToolbar } from "./quickFilterToolbar";

type Props = DataGridPremiumProps & {
  // When set, renders a collapsible quick-filter search box in the toolbar using
  // this text as the placeholder / aria-label. Also turns the toolbar on.
  searchPlaceholder?: string;
  // When set alongside searchPlaceholder, adds a CSV / Excel export menu to the
  // toolbar. Used as the base name of the downloaded file.
  exportFileName?: string;
  // Path to the row's detail page. Use this instead of a hand-rolled onRowClick:
  // a plain click navigates in place, ctrl/cmd+click and middle click open the row
  // in a new tab. The path must be absolute, window.open cannot resolve a
  // route-relative one. Ignored if onRowClick is used on its own.
  getRowHref?: (row: { id: GridRowId }) => string;
};

const openInNewTab = (href: string) =>
  window.open(href, "_blank", "noopener,noreferrer");

export const DataGrid: FC<Props> = ({
  searchPlaceholder,
  exportFileName,
  getRowHref,
  onRowClick,
  slots,
  slotProps,
  showToolbar,
  ...props
}) => {
  const navigate = useNavigate();

  const quickFilterToolbar = useMemo(
    () =>
      searchPlaceholder
        ? makeQuickFilterToolbar(searchPlaceholder, exportFileName)
        : undefined,
    [searchPlaceholder, exportFileName]
  );

  // Read getRowHref through a ref so the row slot props below stay referentially
  // stable when a call site passes an inline arrow, otherwise every row re-renders
  // on every render of the grid's parent.
  const getRowHrefRef = useRef(getRowHref);
  getRowHrefRef.current = getRowHref;
  const hasRowHref = Boolean(getRowHref);

  const handleRowClick = useCallback<
    NonNullable<DataGridPremiumProps["onRowClick"]>
  >(
    (params, event, details) => {
      const href = getRowHrefRef.current?.(params.row);
      if (!href) {
        onRowClick?.(params, event, details);
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        openInNewTab(href);
        return;
      }

      navigate(href);
    },
    [navigate, onRowClick]
  );

  const rowSlotProps = useMemo(() => {
    if (!hasRowHref) {
      return slotProps?.row;
    }

    return {
      ...slotProps?.row,
      onMouseDown: (event: ReactMouseEvent<HTMLDivElement>) => {
        // Suppress the autoscroll a middle press would otherwise start.
        if (event.button === 1) {
          event.preventDefault();
        }
        slotProps?.row?.onMouseDown?.(event);
      },
      // A middle click never reaches onRowClick, so it has to be caught on the row
      // element. GridRow stamps data-id on its root and spreads unknown props onto it.
      onAuxClick: (event: ReactMouseEvent<HTMLDivElement>) => {
        const id = event.currentTarget.getAttribute("data-id");
        const href =
          event.button === 1 && id !== null
            ? getRowHrefRef.current?.({ id })
            : undefined;
        if (href) {
          openInNewTab(href);
        }
        slotProps?.row?.onAuxClick?.(event);
      }
    };
  }, [hasRowHref, slotProps?.row]);

  return (
    <DataGridPremium
      {...props}
      onRowClick={hasRowHref ? handleRowClick : onRowClick}
      showToolbar={showToolbar ?? Boolean(searchPlaceholder)}
      slots={{
        ...(quickFilterToolbar ? { toolbar: quickFilterToolbar } : {}),
        ...slots
      }}
      slotProps={{ ...slotProps, row: rowSlotProps }}
    />
  );
};
